import { buildFormSubmissionPayload, shouldSubmitToServer } from "../form/form";
import {
  applyInitialState,
  getActiveFilterCount,
  readInitialState
} from "./state";

export default class SearchFilter {
  constructor($module) {
    this.$module = $module;
    this.$form = this.$module.querySelector("form");
    this.$toggle = this.$module.querySelector("[data-search-filter-toggle]");
    this.$content = this.$module.querySelector("[data-search-filter-content]");
    this.$count = this.$module.querySelector("[data-search-filter-count]");
    this.$category = this.$module.querySelector("[data-search-filter-category]");
    this.$country = this.$module.querySelector("[data-search-filter-country]");
    this.$county = this.$module.querySelector("[data-search-filter-county-select]");
    this.$divisionSection = this.$module.querySelector(
      "[data-search-filter-division]"
    );
    this.$countySection = this.$module.querySelector("[data-search-filter-county]");
    this.$dublinAreaSection = this.$module.querySelector(
      "[data-search-filter-dublin-area]"
    );
    this.$clearButton = this.$module.querySelector("[data-search-filter-clear]");
    this.$mobileTabletQuery = window.matchMedia("(max-width: 991px)");
    this.initialState = readInitialState(this.$module);
    this.isCollapsed = false;

    if (!this.$category || !this.$country || !this.$county) {
      return;
    }

    applyInitialState(this.initialState, {
      $category: this.$category,
      $country: this.$country,
      $county: this.$county,
      $divisionSection: this.$divisionSection,
      $dublinAreaSection: this.$dublinAreaSection
    });

    this.bindEvents();
    this.syncCollapsibleMode();
    this.updateVisibility();

    if (this.initialState.applied) {
      this.updateAppliedCount();
      return;
    }

    this.resetAppliedCount();
  }

  bindEvents() {
    if (this.$form) {
      this.$form.addEventListener("submit", (event) => this.onSubmit(event));
    }

    this.$category.addEventListener("change", () => this.updateVisibility());
    this.$country.addEventListener("change", () => this.updateVisibility());
    this.$county.addEventListener("change", () => this.updateVisibility());

    if (this.$toggle && this.$content) {
      this.$toggle.addEventListener("click", () => this.onToggleClick());
      this.onQueryChange = () => this.syncCollapsibleMode();

      if (typeof this.$mobileTabletQuery.addEventListener === "function") {
        this.$mobileTabletQuery.addEventListener("change", this.onQueryChange);
      } else if (typeof this.$mobileTabletQuery.addListener === "function") {
        this.$mobileTabletQuery.addListener(this.onQueryChange);
      }
    }

    if (this.$clearButton && this.$form) {
      this.$clearButton.addEventListener("click", (event) => {
        event.preventDefault();
        this.$form.reset();
        this.updateVisibility();
        this.refreshMultiSelectState();
        this.resetAppliedCount();
      });
    }
  }

  onToggleClick() {
    if (!this.isCollapsible()) {
      return;
    }

    this.setCollapsed(!this.isCollapsed);
  }

  isCollapsible() {
    return this.$mobileTabletQuery.matches;
  }

  syncCollapsibleMode() {
    if (!this.$toggle || !this.$content) {
      return;
    }

    const collapsible = this.isCollapsible();

    this.$module.classList.toggle("bc-search-filter--collapsible", collapsible);
    this.$toggle.disabled = !collapsible;

    if (collapsible) {
      this.setCollapsed(true);
      return;
    }

    this.setCollapsed(false);
  }

  setCollapsed(isCollapsed) {
    if (!this.$toggle || !this.$content) {
      return;
    }

    this.isCollapsed = Boolean(isCollapsed);
    this.$module.classList.toggle("bc-search-filter--collapsed", this.isCollapsed);
    this.$toggle.setAttribute("aria-expanded", String(!this.isCollapsed));
    this.$content.hidden = this.isCollapsed;
  }

  updateVisibility() {
    const hasCategory = Boolean(this.$category.value);
    const isIrelandSelected = this.$country.value === "ireland";
    const isDublinSelected = this.$county.value === "dublin";

    this.toggleSection(this.$divisionSection, hasCategory);
    this.toggleSection(this.$countySection, isIrelandSelected);
    this.toggleSection(this.$dublinAreaSection, isIrelandSelected && isDublinSelected);
  }

  toggleSection($section, isVisible) {
    if (!$section) {
      return;
    }

    $section.hidden = !isVisible;
    $section.setAttribute("aria-hidden", String(!isVisible));

    $section.querySelectorAll("select").forEach(($select) => {
      $select.disabled = !isVisible;
    });

    $section.querySelectorAll(".bc-multi-select__control").forEach(($control) => {
      $control.disabled = !isVisible;
    });

    $section.querySelectorAll(".bc-multi-select__input").forEach(($input) => {
      $input.disabled = !isVisible;
    });

    this.refreshMultiSelectState($section);
  }

  refreshMultiSelectState($scope = this.$module) {
    $scope.querySelectorAll(".bc-multi-select__input").forEach(($input) => {
      $input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  onSubmit(event) {
    const { formData, values, multiSelectValues } = buildFormSubmissionPayload(
      this.$form,
      this.$module
    );

    // Make values available in native form submit listeners.
    event.values = values;
    event.multiSelectValues = multiSelectValues;
    this.$form.bcValues = values;
    this.$form.bcMultiSelectValues = multiSelectValues;

    if (!shouldSubmitToServer(this.$form)) {
      event.preventDefault();
    }

    this.updateVisibility();
    this.updateAppliedCount();

    this.$module.dispatchEvent(
      new CustomEvent("search-filter:apply", {
        bubbles: true,
        detail: {
          formData,
          values,
          multiSelectValues
        }
      })
    );
  }

  updateAppliedCount() {
    if (!this.$count) {
      return;
    }

    this.$count.textContent = `(${getActiveFilterCount({
      $category: this.$category,
      $country: this.$country,
      $county: this.$county,
      $divisionSection: this.$divisionSection,
      $dublinAreaSection: this.$dublinAreaSection
    })})`;
  }

  resetAppliedCount() {
    if (!this.$count) {
      return;
    }

    this.$count.textContent = "(0)";
  }
}
